import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { useCompanyCurrency } from '@/contexts/WorkspaceContext';
import {
  stockService, STOCK_UNITS, UNIT_LABEL, type StockItem, type CreateStockItemInput,
} from '@/services/stock.service';

interface Props {
  open: boolean;
  item: StockItem | null;
  categories: string[];
  onClose: () => void;
  onSaved: () => void;
}

export default function StockItemModal({ open, item, categories, onClose, onSaved }: Props) {
  const companyCurrency = useCompanyCurrency();
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [category, setCategory] = useState('');
  const [unit, setUnit] = useState<typeof STOCK_UNITS[number]>('pcs');
  const [quantity, setQuantity] = useState('0');
  const [reorderLevel, setReorderLevel] = useState('0');
  const [reorderQuantity, setReorderQuantity] = useState('');
  const [costPerUnit, setCostPerUnit] = useState('0');
  const [currency, setCurrency] = useState(companyCurrency);
  const [supplier, setSupplier] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (item) {
      setName(item.name); setSku(item.sku ?? ''); setCategory(item.category ?? ''); setUnit(item.unit);
      setQuantity(String(item.quantity)); setReorderLevel(String(item.reorderLevel));
      setReorderQuantity(item.reorderQuantity != null ? String(item.reorderQuantity) : '');
      setCostPerUnit(String(item.costPerUnit)); setCurrency(item.currency ?? companyCurrency);
      setSupplier(item.supplier ?? ''); setIsActive(item.isActive);
    } else {
      setName(''); setSku(''); setCategory(''); setUnit('pcs'); setQuantity('0'); setReorderLevel('0');
      setReorderQuantity(''); setCostPerUnit('0'); setCurrency(companyCurrency); setSupplier(''); setIsActive(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item]);

  const save = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    const qty = Number(quantity);
    const reorder = Number(reorderLevel);
    const cost = Number(costPerUnit);
    if (!item && (Number.isNaN(qty) || qty < 0)) { toast.error('Starting quantity must be zero or more'); return; }
    if (Number.isNaN(reorder) || reorder < 0) { toast.error('Reorder level must be zero or more'); return; }
    if (Number.isNaN(cost) || cost < 0) { toast.error('Cost per unit must be zero or more'); return; }

    setBusy(true);
    const payload: CreateStockItemInput = {
      name: name.trim(),
      sku: sku.trim() || undefined,
      category: category.trim() || undefined,
      unit,
      reorderLevel: reorder,
      reorderQuantity: reorderQuantity.trim() ? Number(reorderQuantity) : undefined,
      costPerUnit: cost,
      currency,
      supplier: supplier.trim() || undefined,
      isActive,
    };
    if (!item) payload.quantity = qty;

    try {
      if (item) await stockService.updateItem(item.stockUid, payload);
      else await stockService.createItem(payload);
      toast.success(item ? 'Item updated' : 'Item added');
      onSaved();
    } catch (e: any) { if (!e?.response) toast.error('Could not save this item.'); }
    finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={() => !busy && onClose()} title={item ? 'Edit stock item' : 'Add stock item'} widthClass="max-w-lg">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField label="Name" required><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Chicken breast" /></FormField>
        <FormField label="SKU" hint="Optional, unique"><Input value={sku} onChange={(e) => setSku(e.target.value)} /></FormField>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField label="Category" hint="Optional">
          <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Meat, Produce…" list="stock-category-suggestions" />
          <datalist id="stock-category-suggestions">
            {categories.map((c) => <option key={c} value={c} />)}
          </datalist>
        </FormField>
        <FormField label="Unit" required>
          <Select value={unit} onChange={(e) => setUnit(e.target.value as typeof unit)}>
            {STOCK_UNITS.map((u) => <option key={u} value={u}>{UNIT_LABEL[u]}</option>)}
          </Select>
        </FormField>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField
          label={item ? 'Current quantity' : 'Starting quantity'}
          hint={item ? 'Use "Record movement" to change this' : undefined}
        >
          <Input type="number" min={0} step="0.001" value={quantity} onChange={(e) => setQuantity(e.target.value)} disabled={!!item} />
        </FormField>
        <FormField label="Reorder level" hint="Flag as low at or below this"><Input type="number" min={0} step="0.001" value={reorderLevel} onChange={(e) => setReorderLevel(e.target.value)} /></FormField>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField label="Reorder quantity" hint="Optional"><Input type="number" min={0} step="0.001" value={reorderQuantity} onChange={(e) => setReorderQuantity(e.target.value)} /></FormField>
        <FormField label="Cost per unit">
          <div className="flex gap-2">
            <div className="min-w-0 flex-1"><Input type="number" min={0} step="0.0001" value={costPerUnit} onChange={(e) => setCostPerUnit(e.target.value)} placeholder="0.00" /></div>
            <div className="w-20 shrink-0"><Input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} className="text-center uppercase" aria-label="Currency" /></div>
          </div>
        </FormField>
      </div>
      <FormField label="Supplier" hint="Optional"><Input value={supplier} onChange={(e) => setSupplier(e.target.value)} /></FormField>
      {item && (
        <FormField label="Status">
          <Select value={isActive ? 'active' : 'inactive'} onChange={(e) => setIsActive(e.target.value === 'active')}>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </Select>
        </FormField>
      )}
      <div className="mt-4 flex justify-end gap-2 border-t border-border pt-4">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={save} loading={busy} disabled={busy}>{item ? 'Save' : 'Add'}</Button>
      </div>
    </Modal>
  );
}
